// src/adm/api.js
const isProd = process.env.NODE_ENV === "production";

const API_BASE = process.env.REACT_APP_API_BASE || "";

function abs(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const b = (API_BASE || "").replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return b ? `${b}${p}` : p;
}

// ✅ 관리자 토큰 공통 헬퍼
function admAuthHeaders() {
  if (typeof localStorage === "undefined") return {};
  const t = localStorage.getItem("adm_token") || localStorage.getItem("token") || "";
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function json(url, init = {}) {
  const res = await fetch(url, init);
  const ct = res.headers.get("content-type") || "";
  const raw = await res.text();
  let data = null;
  if (ct.includes("application/json")) {
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {}
  }
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      alert("관리자 인증이 만료되었거나 권한이 없습니다. 다시 로그인해 주세요.");
      if (typeof window !== "undefined") window.location.href = "/adm/login";
    }
    throw new Error(data?.error?.message || data?.message || `HTTP ${res.status} - ${raw?.slice(0, 160)}`);
  }
  return data;
}

/** ================== Auth ================== */
export const AdmAuthAPI = {
  async login({ id, password }) {
    const data = await json(abs("/auth/login_admin"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, password }),
      credentials: "include",
    });
    if (!data?.success || !data?.data?.token) throw new Error("로그인 실패(응답 형식)");
    return data.data;
  },
  logout() {
    try {
      localStorage.removeItem("adm_token");
    } catch {}
  },
};

/** ================== Branch ================== */
export const AdmBranchAPI = {
  // 지점 리스트
  async list({ branch_idx } = {}) {
    const qs = new URLSearchParams();
    if (branch_idx) qs.set("branch_idx", String(branch_idx));

    const data = await json(abs(`/api/getBranch${qs.toString() ? `?${qs}` : ""}`), {
      method: "GET",
      headers: { ...admAuthHeaders() },
      credentials: "include",
    });

    if (!data?.success || !Array.isArray(data?.data)) throw new Error("지점 리스트 로드 실패");
    return data.data;
  },

  async toggleStatus({ branch_idx, status }) {
    const payload = {
      branch_idx,
      status: typeof status === "string" ? Number(status) : status, // 1 or 9
    };
    const data = await json(abs("/api/updateBranch"), {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...admAuthHeaders() },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!data?.success) throw new Error(data?.error?.message || "상태 변경 실패");
    return true;
  },

  // 지점 추가
  async create({ branch_name, branch_eng, branch_area, area_sort, status }) {
    const payload = {
      branch_name,
      branch_eng,
      branch_area,
      area_sort,
      status: typeof status === "string" ? Number(status) : status,
    };
    const data = await json(abs("/api/createBranch"), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...admAuthHeaders() },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!data?.success) throw new Error(data?.error?.message || "지점 추가 실패");
    return true;
  },
};

/** ================== Users ================== */
export const AdmUserAPI = {
  async list({ branch_name, keyword } = {}) {
    const qs = new URLSearchParams();
    if (branch_name) qs.set("branch_name", branch_name);
    if (keyword) qs.set("keyword", keyword);

    const data = await json(abs(`/api/getUser${qs.toString() ? `?${qs}` : ""}`), {
      method: "GET",
      headers: { ...admAuthHeaders() },
      credentials: "include",
    });

    const rows = (Array.isArray(data) && data) || (Array.isArray(data?.data) && data.data) || [];
    return rows;
  },

  async toggleStatus({ id, status }) {
    const body = {
      id,
      status: typeof status === "string" ? Number(status) : status,
    };
    const data = await json(abs("/api/updateUser"), {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...admAuthHeaders() },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!data?.success) throw new Error(data?.error?.message || "상태 변경 실패");
    return true;
  },

  async create(payload) {
    const data = await json(abs("/auth/register"), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...admAuthHeaders() },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!data?.success) throw new Error(data?.error?.message || "회원 추가 실패");
    return true;
  },

  async deleteUser(id) {
    const data = await json(abs("/api/deleteUser"), {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...admAuthHeaders() },
      credentials: "include",
      body: JSON.stringify({ id }),
    });
    if (!data?.success) throw new Error(data?.error?.message || "회원 삭제 실패");
    return true;
  },
};

/** ================== Logs ================== */
export const AdmLogAPI = {
  async dayCounts({ branch_idx, year_month }) {
    if (!year_month) throw new Error("year_month(YYYY-MM)가 필요합니다.");

    const qs = new URLSearchParams();
    qs.set("year_month", year_month);
    if (branch_idx) qs.set("branch_idx", String(branch_idx));

    const data = await json(abs(`/api/getApiCallDayCount?${qs.toString()}`), {
      method: "GET",
      headers: { ...admAuthHeaders() },
      credentials: "include",
    });

    // 관대 파싱 (배열 또는 {data: []})
    return (Array.isArray(data) && data) || (Array.isArray(data?.data) && data.data) || [];
  },
};

/** ================== Time Logs ================== */
export const AdmTimeLog = {
  async hourCounts({ branch_idx, date }) {
    if (!date) throw new Error("time(YYYY-MM-DD)가 필요합니다.");

    const qs = new URLSearchParams();
    qs.set("time", date); // YYYY-MM-DD
    // 지점 미선택이면 전체 조회 → 파라미터 제외
    if (branch_idx) qs.set("branch_idx", String(branch_idx));

    const data = await json(abs(`/api/getApiCallTimeCount?${qs.toString()}`), {
      method: "GET",
      headers: { ...admAuthHeaders() },
      credentials: "include",
    });

    // 관대 파싱: 배열 또는 {data: []}
    return (Array.isArray(data) && data) || (Array.isArray(data?.data) && data.data) || [];
  },
};
